import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Button,
  Form,
  Modal,
  notification,
  Popconfirm,
  Radio,
  Select,
  Space,
  Table,
  Tooltip,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { EditOutlined, SaveOutlined } from '@ant-design/icons';
import FormItem from 'antd/es/form/FormItem';
import Column from '../../helpers/columns';
import { FormContext } from '../../hooks/useForm';
import {
  disableTitle,
  getPeriodForTransaction,
  getTitlesById,
  updateLinesTitle,
  updateZonalTitle,
} from '../../services/accounts/accounts';
import useDatasource from '../../hooks/useDatasource';
import AuthContext, { hasPermission } from '../auth';
import { ProductCategories } from './TitlesDescandantsList';
import { getAdminUnits } from '../../services/taxonomies/zonal-restriction';
import ZonalRestriction from './ZonalRestriction';
import LinesRestriction from './LinesRestriction';

export const CustomerTypes = {
  content: [
    { id: 'CERERE', name: 'Cerere' },
    { id: 'CORECTIE', name: 'Corectie' },
  ],
};

const TransportTitles = () => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const { permissions } = user;

  const form = useContext(FormContext);
  const { value } = form;
  const [disableTitleForm] = Form.useForm();
  const [editZonalUats] = Form.useForm();
  const [editLinesForm] = Form.useForm();
  const { setFieldsValue, resetFields } = disableTitleForm;
  const { id } = value;
  const [showModal, setShowModal] = useState(false);
  const [showZonalModal, setShowZonalModal] = useState(false);
  const [showLinesModal, setShowLinesModal] = useState(false);
  const [period, setPeriod] = useState([]);
  const [title, setTitle] = useState(null);
  const [selectedDate, setDate] = useState(1);

  const { pagination, content, handleChange, reload } = useDatasource((arg) =>
    getTitlesById(id, arg).then((response) => {
      response?.content?.forEach((rTitle) => {
        if (rTitle.productType === 'PURSE') {
          // eslint-disable-next-line no-param-reassign
          rTitle.quantity = `${parseFloat(rTitle.purseValue / 100).toFixed(
            2,
          )} RON`;
        }
      });
      return response;
    }),
  );

  const metropolitanEnabled = useMemo(
    () => window._env_.METROPOLITAN_ENABLE === 'true',
    [],
  );

  const actionsColumn = Column.actions(
    t('table.actions'),
    (val) => (
      <span style={{ textAlign: 'right' }}>
        <Space>
          <Tooltip title={t('actions.inactivate')}>
            <Button
              className="ant-btn-success"
              type="primary"
              icon={<SaveOutlined />}
              disabled={val.deleted}
              onClick={() => {
                getPeriodForTransaction(val.id).then((res) => {
                  setPeriod(res);
                  if (res.canEditB) {
                    setDate(2);
                    setFieldsValue({ dateType: 2 });
                  }
                  if (res.canEditA) {
                    setFieldsValue({ dateType: 1 });
                    setDate(1);
                  }
                  setTitle(val);
                  setShowModal(true);
                });
              }}
              size="medium"
            >
              {t('actions.inactivate')}
            </Button>
          </Tooltip>
          {metropolitanEnabled &&
          val?.productType === 'PASS' &&
          val?.transitionsNo != null ? (
            <Tooltip title={t('actions.modifyZonalUat')}>
              <Button
                className="ant-btn-success"
                type="primary"
                icon={<SaveOutlined />}
                disabled={val.deleted}
                onClick={() => {
                  getPeriodForTransaction(val.id).then((res) => {
                    if (res.canEditA || res.canEditB) {
                      setTitle(val);
                      setShowZonalModal(true);
                    } else {
                      notification.error({
                        message: t('errors.noActivPeriodTOUpdateUats'),
                      });
                    }
                  });
                }}
                size="medium"
              >
                {t('actions.updateZonal')}
              </Button>
            </Tooltip>
          ) : (
            <div />
          )}
          {val?.lines &&
          val.lines
            .split(',')
            .map((group) => group.trim())
            .filter(Boolean).length >= 1 ? (
            <Tooltip title={t('actions.modifyLines')}>
              <Button
                className="ant-btn-success"
                type="primary"
                icon={<EditOutlined />}
                disabled={val.deleted}
                onClick={() => {
                  console.log('val', val);
                  getPeriodForTransaction(val.id).then((res) => {
                    if (res.canEditA || res.canEditB) {
                      setTitle(val);
                      setShowLinesModal(true);
                    } else {
                      notification.error({
                        message: t('errors.noActivPeriodToUpdateLines'),
                      });
                    }
                  });
                }}
                size="medium"
              >
                {t('actions.updateLines')}
              </Button>
            </Tooltip>
          ) : (
            <div />
          )}
        </Space>
      </span>
    ),
    {
      sort: false,
      width: metropolitanEnabled ? 380 : 260,
    },
  );

  const [uatsDictionary, setUatsDictionary] = useState({
    content: [],
    loading: false,
    error: false,
  });

  useEffect(() => {
    let cancelled = false;

    if (!metropolitanEnabled) {
      setUatsDictionary({
        content: [],
        loading: false,
        error: false,
      });

      return () => {
        cancelled = true;
      };
    }

    setUatsDictionary({
      content: [],
      loading: true,
      error: false,
    });

    const loadUats = async () => {
      try {
        const data = await getAdminUnits();
        if (!cancelled) {
          setUatsDictionary({
            content: data,
            loading: false,
            error: false,
          });
        }
      } catch (e) {
        if (!cancelled) {
          setUatsDictionary({
            content: [],
            loading: false,
            error: true,
          });
        }
      }
    };

    loadUats();

    return () => {
      cancelled = true;
    };
  }, [metropolitanEnabled]);

  const handleOk = useCallback(() => {
    const product = editZonalUats.getFieldValue('product');
    const uats = (() => {
      switch (product?.transitionsNo) {
        case 0:
          return [product?.zonal?.option1];
        case 1:
          return [product?.zonal?.option1, product?.zonal?.option2];
        case 2:
          return (
            product?.axis?.administrativeUnits?.map((unit) => unit.id) ?? []
          );
        default:
          return [];
      }
    })();

    const payload = {
      axisId: product?.zonal?.axisId ?? null,
      option1: product?.zonal?.option1 ?? null,
      option2: product?.zonal?.option2 ?? null,
      allUats: uats,
    };

    Modal.confirm({
      title: t('actions.confirmUpdateZonalTitle'),
      content: t('actions.confirmUpdateZonalBody'),
      okText: t('actions.confirm'),
      cancelText: t('actions.cancel'),
      onOk: () =>
        updateZonalTitle(product?.titleId, payload).then(() => {
          setShowZonalModal(false);
          editZonalUats.resetFields();
          reload();
        }),
    });
  }, [editZonalUats, reload, t]);

  const handleLinesOk = useCallback(() => {
    const values = editLinesForm.getFieldsValue();
    const payload = {
      lines: (values?.lines || []).filter((v) => v != null),
    };

    Modal.confirm({
      title: t('actions.confirmUpdateLinesTitle'),
      content: t('actions.confirmUpdateLinesBody'),
      okText: t('actions.confirm'),
      cancelText: t('actions.cancel'),
      onOk: () =>
        updateLinesTitle(values?.titleId, payload).then(() => {
          setShowLinesModal(false);
          editLinesForm.resetFields();
          reload();
        }),
    });
  }, [editLinesForm, reload, t]);

  const columns = useMemo(() => {
    return [
      Column.text('productId', t('entity.account.transportTitle.product_id'), {
        filter: false,
        width: 50,
      }),
      Column.text(
        'productName',
        t('entity.account.transportTitle.product_name'),
        {
          filter: true,
        },
      ),
      Column.dictionary(
        'productType',
        t('entity.account.transportTitle.product_types._'),
        ProductCategories,
        {
          width: 150,
          filter: true,
          dictLabel: (entry) =>
            t(`entity.account.transportTitle.product_types.${entry.id}`),
        },
      ),
      Column.text(
        'validPeriod',
        t('entity.account.transportTitle.validPeriod'),
        {
          sort: false,
          width: 600,
        },
      ),
      Column.text(
        'lineGroups',
        t('entity.account.transportTitle.line_groups'),
        {
          filter: false,
        },
      ),
      Column.text('lines', t('entity.account.transportTitle.lines'), {
        filter: true,
      }),
      Column.text('quantity', t('entity.account.transportTitle.quantity'), {
        sort: false,
      }),
      Column.bool('deleted', t('entity.account.transportTitle.deleted._'), {
        filter: true,
        inverted: true,
        labels: [
          t('table.filter.all'),
          t('entity.account.transportTitle.deleted.false'),
          t('entity.account.transportTitle.deleted.true'),
          t('table.filter.unknown'),
        ],
      }),
      Column.bool('active', t('entity.account.transportTitle.active._'), {
        filter: true,
        inverted: false,
        labels: [
          t('table.filter.all'),
          t('entity.account.transportTitle.active.true'),
          t('entity.account.transportTitle.active.false'),
          t('table.filter.unknown'),
        ],
      }),
      ...(metropolitanEnabled
        ? [
            Column.dictionary(
              'uatStart',
              t('components.salesReport.uatStart'),
              uatsDictionary,
            ),
            Column.dictionary(
              'uatStop',
              t('components.salesReport.uatStop'),
              uatsDictionary,
            ),
          ]
        : []),
      hasPermission(permissions, [
        'SYS_SUPERUSER',
        'ADMINISTRATION_ACCOUNTS_PERMISSION',
      ])
        ? actionsColumn
        : {},
    ];
  }, [t, permissions, actionsColumn, metropolitanEnabled, uatsDictionary]);

  const contentWithProdType = useMemo(
    () =>
      content.map((ttl) => ({
        ...ttl,
        prodTypeName: t(
          `entity.account.transportTitle.product_types.${ttl.productType}`,
        ),
      })),

    [content, t],
  );

  return (
    <>
      {showModal && (
        <Modal
          title={t('entity.account.transportTitle.deactivateTitle')}
          width={350}
          visible
          closable={false}
          footer={[
            <Popconfirm
              key="enableTitle"
              placement="topLeft"
              title={t(
                'entity.account.transportTitle.deactivatePeriodConfirmation',
                {
                  productName: title.productName,
                },
              )}
              okText={t('entity.account.transportTitle.active.true')}
              cancelText={t('entity.account.transportTitle.active.false')}
              okButtonProps={{ htmlType: 'submit', type: 'primary' }}
              onConfirm={() => {
                disableTitleForm.validateFields().then((r) => {
                  disableTitle(title.id, r).then(() => {
                    setShowModal(false);
                    resetFields();
                    reload();
                  });
                });
              }}
            >
              {(period.canEditA || period.canEditB) && (
                <Tooltip title={t('actions.cancelPeriod')}>
                  <Button
                    className="ant-btn-success"
                    type="primary"
                    icon={<SaveOutlined />}
                  >
                    {t('actions.cancelPeriod')}
                  </Button>
                </Tooltip>
              )}
            </Popconfirm>,
            <Button
              key="submit"
              type="primary"
              onClick={() => {
                setShowModal(false);
                resetFields();
              }}
            >
              {t('actions.cancelInactivation')}
            </Button>,
          ]}
        >
          {' '}
          {period.canEditA || period.canEditB ? (
            <>
              <Form form={disableTitleForm}>
                <Radio.Group
                  onChange={(e) => {
                    setDate(e.target.value);
                    setFieldsValue({ dateType: e.target.value });
                  }}
                  defaultValue={selectedDate}
                >
                  <FormItem name="dateType">
                    <Space direction="vertical">
                      {period.canEditA && (
                        <Radio value={1}>
                          {period.startDateA} - {period.endDateA}
                        </Radio>
                      )}
                      {period.canEditB && (
                        <Radio value={2}>
                          {' '}
                          {period.startDateB} - {period.endDateB}
                        </Radio>
                      )}
                    </Space>
                  </FormItem>
                </Radio.Group>

                <FormItem
                  label={t('entity.account.transportTitle.deactivationReason')}
                  name="reason"
                  rules={[
                    {
                      required: true,
                      message: t('errors.requiredField'),
                    },
                  ]}
                >
                  <Select
                    // loading={CustomerTypes.loading}
                    onChange={(e) => {
                      setFieldsValue({ reason: e });
                    }}
                  >
                    {CustomerTypes.content.map((option) => (
                      <Select.Option value={option.id} key={option.id}>
                        {option.name}
                      </Select.Option>
                    ))}
                  </Select>
                </FormItem>
              </Form>
            </>
          ) : (
            t('entity.account.transportTitle.titleCannotBeDeactivated')
          )}
        </Modal>
      )}
      {showZonalModal && (
        <Modal
          title={t('actions.updateZonalTitle')}
          width={450}
          visible
          closable={false}
          footer={[
            <Button
              className="ant-btn-success"
              key="ok"
              type="primary"
              onClick={() => {
                editZonalUats.validateFields().then((values) => {
                  handleOk(values);
                });
              }}
            >
              OK
            </Button>,
            <Button
              key="submit"
              type="primary"
              onClick={() => {
                setShowZonalModal(false);
                editZonalUats.resetFields();
              }}
            >
              Renunta
            </Button>,
          ]}
        >
          <ZonalRestriction form={editZonalUats} title={title} />
        </Modal>
      )}
      {showLinesModal && (
        <Modal
          title={t('actions.modifyLinesTitle')}
          width={450}
          visible
          closable={false}
          footer={[
            <Button
              className="ant-btn-success"
              key="ok"
              type="primary"
              onClick={() => {
                editLinesForm.validateFields().then(() => {
                  handleLinesOk();
                });
              }}
            >
              OK
            </Button>,
            <Button
              key="cancel"
              type="primary"
              onClick={() => {
                setShowLinesModal(false);
                editLinesForm.resetFields();
              }}
            >
              {t('actions.cancel')}
            </Button>,
          ]}
        >
          <LinesRestriction form={editLinesForm} title={title} />
        </Modal>
      )}
      <Table
        columns={columns}
        rowKey="id"
        // loading={loading}
        pagination={pagination}
        dataSource={contentWithProdType}
        onChange={handleChange}
        scroll={{ x: 700 }}
      />
    </>
  );
};
export default TransportTitles;
